<template>
  <main
    id="stock"
    class="page-container stock-list"
    v-if="$route.name === 'listini-category'"
  >
    <div class="table-container-stock">
      <div class="items-toolbar">
        <button
          class="primary-btn"
          @click="openNewArticle"
          :disabled="isLoading"
        >
          <i class="fa-solid fa-plus"></i> Nuovo articolo
        </button>
        <div class="items-toolbar__right">
          <StatusSelect
            v-if="category === 'bagno' || category === 'piastrelle'"
            v-model="selectedSubCategory"
            :options="subCategoryFilterOptions"
            placeholder="Tutte le tipologie"
            placeholderIcon="fa-filter"
            :disabled="isLoading"
          />
          <!-- [sc-48248] Filtro per marca, chiesto dai Listini -->
          <StatusSelect
            v-if="brandFilterOptions.length > 1"
            v-model="selectedBrand"
            :options="brandFilterOptions"
            placeholder="Tutti i brand"
            placeholderIcon="fa-tag"
            :disabled="isLoading"
          />
          <button
            class="refresh-btn"
            :class="{ refreshed }"
            @click="handleRefresh"
            :disabled="refreshing || isLoading"
            title="Aggiorna"
          >
            <i
              :class="[
                'fa-solid',
                refreshed ? 'fa-check' : 'fa-arrows-rotate',
                { 'fa-spin': refreshing },
              ]"
            ></i>
            <span>{{
              refreshing ? "Aggiorna…" : refreshed ? "Aggiornato!" : "Aggiorna"
            }}</span>
          </button>
        </div>
      </div>
      <!-- [sc-48245] L'overlay era in cima a `.table-container-stock`, quindi
           `inset: 0` lo stendeva su tutta la pagina: sfocava anche il campo di
           ricerca in fondo, cioè proprio quello in cui si sta digitando quando
           parte il caricamento automatico. Ora è confinato alla tabella, come
           già fatto in Magazzino con sc-48061. -->
      <div class="listini-table-region">
        <div
          v-if="isLoading"
          class="loader-overlay"
          aria-live="polite"
          aria-busy="true"
        >
          <div class="spinner" role="status"></div>
          <p>Carico gli articoli…</p>
        </div>

        <div class="tbl-wrap" :class="{ dimmed: isLoading }">
          <table class="tbl item-table">
            <thead>
              <tr>
                <th v-for="col in columns" :key="col.key">{{ col.label }}</th>
                <th style="text-align: left">Azioni</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="(item, index) in items" :key="index">
                <tr class="item-row">
                  <td
                    v-for="col in columns"
                    :key="col.key"
                    :class="{ 'td-name': col.truncate }"
                    :title="col.truncate ? item[col.key] ?? '' : undefined"
                  >
                    <!-- [sc-48245] Senza prezzo si stampa "—" e basta: il
                         suffisso di unità darebbe "— /pz", che sembra un
                         errore di composizione. -->
                    <span v-if="col.format === 'price'"
                      >{{ formatPriceToPrint(item[col.key])
                      }}<template v-if="item[col.key] != null">{{
                        priceUnit(item)
                      }}</template></span
                    >
                    <span v-else-if="col.format === 'status'">
                      <span :class="`badge badge--${item[col.key]}`">{{
                        item[col.key]
                      }}</span>
                    </span>
                    <span v-else-if="col.format === 'dimensions'">
                      {{
                        item.widthCm && item.heightCm
                          ? `${item.widthCm}×${item.heightCm}`
                          : "—"
                      }}
                    </span>
                    <span v-else-if="col.format === 'scelta'">{{
                      item.specs?.scelta ?? "—"
                    }}</span>
                    <span v-else-if="col.format === 'decimal2'">{{
                      item[col.key] != null && item[col.key] !== ""
                        ? formatDecimal(item[col.key])
                        : "—"
                    }}</span>
                    <span v-else>{{ item[col.key] ?? "—" }}</span>
                  </td>
                  <td class="actions-cell">
                    <button
                      class="icons-button"
                      @click="goToEditItem(item.id)"
                      title="Modifica articolo"
                    >
                      <i class="fa-solid fa-pen-to-square"></i>
                    </button>
                    <button
                      class="icons-button icons-button--danger"
                      @click="openArchiveModal(item)"
                      title="Archivia articolo"
                    >
                      <i class="fa-solid fa-trash-can"></i>
                    </button>
                  </td>
                </tr>
              </template>
              <tr v-if="items.length === 0">
                <td :colspan="columns.length + 1" class="no-result">
                  Nessun articolo trovato.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="pagination">
        <div class="pagination-left">
          <div class="search-container">
            <i
              v-if="!searchTerm"
              class="fa-solid fa-magnifying-glass search-icon"
            ></i>
            <input
              type="text"
              v-model="searchTerm"
              @keyup.enter="onSearchNow"
              placeholder="Cerca per codice o nome..."
              class="search-box"
            />
            <button
              v-if="searchTerm"
              @click="onResetSearch"
              class="clear-search-button"
              :disabled="isLoading"
            >
              <i class="fa-solid fa-times"></i>
            </button>
          </div>
        </div>
        <!-- [sc-48337] Avanti/indietro al posto dei numeri di pagina: con la
             paginazione a cursore il database non sa quante pagine ci sono, e
             non doverlo sapere è esattamente ciò che la rende costante al
             crescere del catalogo. Resta il "torna all'inizio". -->
        <div class="pagination-right">
          <div class="pagination-info">
            {{ positionLabel }} di {{ totalLabel }}
          </div>
          <div class="pagination-controls">
            <button
              class="icons-button"
              @click="goToFirstPage"
              :disabled="isLoading || !pagination.hasPrev"
              title="Torna all'inizio"
            >
              <i class="fa-solid fa-angles-left"></i>
            </button>
            <button
              class="icons-button"
              @click="goToAdjacentPage('prev')"
              :disabled="isLoading || !pagination.hasPrev"
              title="Precedenti"
            >
              <i class="fa-solid fa-chevron-left"></i>
            </button>
            <button
              class="icons-button"
              @click="goToAdjacentPage('next')"
              :disabled="isLoading || !pagination.hasNext"
              title="Successivi"
            >
              <i class="fa-solid fa-chevron-right"></i>
            </button>
            <button
              class="icons-button"
              @click="goToLastPage"
              :disabled="isLoading || !pagination.hasNext"
              title="Vai alla fine"
            >
              <i class="fa-solid fa-angles-right"></i>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal archiviazione -->
    <VueFinalModal
      v-model="showArchiveModal"
      :clickToClose="!archiving"
      :escToClose="!archiving"
      classes="documents-modal"
      content-class="confirm-modal-content modal--archive"
    >
      <template #default>
        <button
          class="modal__close icons-button"
          @click="closeArchiveModal"
          :disabled="archiving"
        >
          <i class="fa-solid fa-xmark"></i>
        </button>
        <h3 class="modal__title">Archivia articolo</h3>
        <p class="modal__subtitle">
          Archiviare
          <strong>{{ selectedItem?.itemName || selectedItem?.brand }}</strong
          >?
        </p>
        <div class="modal-actions">
          <button
            class="secondary-btn"
            @click="closeArchiveModal"
            :disabled="archiving"
          >
            Annulla
          </button>
          <button
            class="primary-btn confirm-btn"
            @click="confirmArchive"
            :disabled="archiving"
          >
            <span v-if="archiving">Archivia…</span>
            <span v-else>Archivia</span>
          </button>
        </div>
      </template>
    </VueFinalModal>

    <!-- Modal selezione tipologia (piastrelle) -->
    <VueFinalModal
      v-model="showSubCategoryModal"
      :clickToClose="true"
      :escToClose="true"
      classes="documents-modal"
      content-class="confirm-modal-content"
    >
      <template #default>
        <button
          class="modal__close icons-button"
          @click="showSubCategoryModal = false"
        >
          <i class="fa-solid fa-xmark"></i>
        </button>
        <h3 class="modal__title">Seleziona tipologia</h3>
        <p class="modal__subtitle">
          Scegli la tipologia prima di creare il nuovo articolo.
        </p>
        <div class="field" style="margin: 1rem 0">
          <StatusSelect
            v-model="pendingSubCategory"
            :options="subCategoryOptions"
            placeholder="Seleziona tipologia..."
            placeholderIcon="fa-tag"
          />
        </div>
        <div class="modal-actions">
          <button class="secondary-btn" @click="showSubCategoryModal = false">
            Annulla
          </button>
          <button
            class="primary-btn confirm-btn"
            @click="confirmSubCategory"
            :disabled="!pendingSubCategory"
          >
            Continua
          </button>
        </div>
      </template>
    </VueFinalModal>
  </main>
  <router-view />
</template>

<script>
import { mapActions, mapGetters } from "vuex/dist/vuex.cjs.js";
import debounce from "lodash/debounce";
import { formatPriceToPrint, formatDecimal } from "../utilities/formatting";
import { variantLabel } from "../utilities/itemVariant";
import { bagnoSubCategoryOptions } from "../utilities/bagnoSubCategories";
import StatusSelect from "@/components/StatusSelect.vue";

const COLUMN_GROUPS = {
  area: [
    "piastrelle",
    "rivestimenti parete",
    "parquet",
    "laminati",
    "decking esterno",
  ],
  piece: ["sanitari", "rubinetteria", "box doccia", "accessori bagno"],
  linear: ["profili", "sistemi di scarico"],
  furniture: ["mobili", "porte e infissi"],
  bath: ["bagno"],
};

const COLUMNS = {
  area: [
    { key: "displayCode", label: "Codice", truncate: true },
    { key: "brand", label: "Brand", truncate: true },
    { key: "serie", label: "Serie", truncate: true },
    { key: "itemName", label: "Nome", truncate: true },
    { key: "subCategory", label: "Tipologia", truncate: true },
    { key: "formatLabel", label: "Formato" },
    { key: "piecesPerBox", label: "Pz/sc" },
    { key: "sqmPerBox", label: "M²/sc", format: "decimal2" },
    { key: "mlPerBox", label: "ML/sc", format: "decimal2" },
    { key: "price", label: "Prezzo", format: "price" },
  ],
  piece: [
    { key: "displayCode", label: "Codice", truncate: true },
    { key: "brand", label: "Brand", truncate: true },
    { key: "serie", label: "Serie", truncate: true },
    { key: "itemName", label: "Nome", truncate: true },
    { key: "fixtureType", label: "Tipo" },
    { key: "fixturePosition", label: "Posizione" },
    { key: "finish", label: "Finitura", truncate: true },
    { key: "price", label: "Prezzo", format: "price" },
  ],
  linear: [
    { key: "displayCode", label: "Codice", truncate: true },
    { key: "brand", label: "Brand", truncate: true },
    { key: "serie", label: "Serie", truncate: true },
    { key: "itemName", label: "Nome", truncate: true },
    { key: "lengthMm", label: "Lung. mm" },
    { key: "mlPerBox", label: "ML/sc", format: "decimal2" },
    { key: "material", label: "Materiale", truncate: true },
    { key: "price", label: "Prezzo", format: "price" },
  ],
  furniture: [
    { key: "displayCode", label: "Codice", truncate: true },
    { key: "brand", label: "Brand", truncate: true },
    { key: "serie", label: "Serie", truncate: true },
    { key: "itemName", label: "Nome", truncate: true },
    { key: "dimensions", label: "Dim.", format: "dimensions" },
    { key: "material", label: "Materiale", truncate: true },
    { key: "finish", label: "Finitura", truncate: true },
    { key: "price", label: "Prezzo", format: "price" },
  ],
  bath: [
    { key: "displayCode", label: "Codice", truncate: true },
    { key: "brand", label: "Brand", truncate: true },
    { key: "itemName", label: "Nome", truncate: true },
    { key: "subCategory", label: "Tipologia", truncate: true },
    // [sc-48247] Senza questa colonna le varianti dello stesso codice sono
    // righe identiche con prezzi diversi: vedi variantLabel().
    { key: "variantLabel", label: "Variante", truncate: true },
    { key: "price", label: "Prezzo", format: "price" },
  ],
  generic: [
    { key: "displayCode", label: "Codice", truncate: true },
    { key: "brand", label: "Brand", truncate: true },
    { key: "itemName", label: "Nome", truncate: true },
    { key: "notes", label: "Descrizione", truncate: true },
    { key: "price", label: "Prezzo", format: "price" },
  ],
};

const PARQUET_COLUMNS = [
  { key: "displayCode", label: "Codice", truncate: true },
  { key: "brand", label: "Brand", truncate: true },
  { key: "serie", label: "Serie", truncate: true },
  { key: "itemName", label: "Nome", truncate: true },
  { key: "material", label: "Materiale", truncate: true },
  { key: "finish", label: "Finitura", truncate: true },
  { key: "scelta", label: "Scelta", format: "scelta" },
  { key: "formatLabel", label: "Formato" },
  { key: "thicknessMm", label: "Sp. mm" },
  { key: "price", label: "Prezzo", format: "price" },
];

function getCategoryGroup(category) {
  for (const [group, cats] of Object.entries(COLUMN_GROUPS)) {
    if (cats.includes(category)) return group;
  }
  return "generic";
}

// [sc-48222] Le tipologie bagno sono normalizzate a DB: un solo valore
// canonico per concetto (singolare, Title Case). La mappa di alias che stava
// qui — il cerotto di sc-48199 che faceva cercare al filtro tutte le varianti
// storiche insieme — non serve più ed è stata rimossa, non svuotata.
//
// Il punto di verità è `src/scripts/lib/bagno-subcategory.cjs` lato API.
// [sc-48245] La lista che stava qui è stata spostata in
// `src/utilities/bagnoSubCategories.js`: da quando serve anche alla modale
// "crea articolo" del preventivo, tenerne due copie allineate a mano era una
// di troppo.

export default {
  name: "ListiniCategoryPage",
  components: { StatusSelect },
  props: {
    category: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      isLoading: false,
      // [sc-48337] Stato della paginazione a cursore. `pageIndex` serve solo a
      // scrivere "17-24 di": a cursore l'API non sa a che pagina siamo.
      // `currentCursor` è quello che ha prodotto la pagina mostrata, e serve a
      // ricaricarla dopo un'archiviazione o un aggiornamento.
      pageIndex: 1,
      currentCursor: null,
      currentDirection: "next",
      // [sc-48337] Da dove si sta contando: "start" dalla prima pagina, "end"
      // dall'ultima. `pageIndex` è il numero di pagina **a partire da
      // quell'estremo**, quindi vale 1 sia sulla prima sia sull'ultima.
      //
      // Serve perché l'ultima pagina a cursore è piena e non allineata alla
      // griglia: ancorare il conto alla fine è l'unico modo di numerarla anche
      // quando il totale è fermo a 10.000 e la posizione assoluta non si sa.
      anchor: "start",
      searchTerm: "",
      selectedItem: null,
      showArchiveModal: false,
      archiving: false,
      refreshing: false,
      refreshed: false,
      showSubCategoryModal: false,
      pendingSubCategory: "",
      selectedSubCategory: "",
      // [sc-48248] Marche della categoria aperta: arrivano dall'API, non dagli
      // articoli a video, che sono otto per pagina.
      selectedBrand: "",
      brands: [],
      piastrelleSubCategoryOptions: [
        { value: "Elementi Lineari", label: "Elementi Lineari" },
        { value: "Decori", label: "Decori" },
        { value: "Fondi", label: "Fondi" },
        { value: "Battiscopa", label: "Battiscopa" },
        { value: "Listelli", label: "Listelli" },
        { value: "Gradini", label: "Gradini" },
        { value: "Scalini", label: "Scalini" },
        { value: "Altri Pezzi Speciali", label: "Altri Pezzi Speciali" },
      ],
      // [sc-48222] Tassonomia canonica SINGOLARE della categoria bagno: sono
      // esattamente i valori presenti a DB dopo la normalizzazione, usati sia
      // dal filtro sia dal modale "Seleziona tipologia".
      //
      // ⚠️ Deve restare allineata a `src/scripts/lib/bagno-subcategory.cjs`
      // lato API, che è il punto di verità per gli importer. Una voce in più
      // qui crea articoli con una tipologia che nessun listino produce; una in
      // meno rende invisibili al filtro gli articoli che la usano.
      bagnoSubCategoryOptions: bagnoSubCategoryOptions(),
    };
  },
  computed: {
    ...mapGetters({
      allItems: "items/list",
      pagination: "items/pagination",
    }),
    items() {
      // SC-47820 — codice visibile: supplierCode (fornitore) se presente,
      // altrimenti itemCode. item_code tecnico (BERLONI-<HASH>) mai mostrato.
      return this.allItems
        .filter((i) => i.category === this.category)
        .map((i) => ({
          ...i,
          displayCode: i.supplierCode || i.itemCode,
          // [sc-48247] Usata solo dalle colonne `bath`; altrove è ignorata.
          variantLabel: variantLabel(i),
        }));
    },
    columns() {
      if (this.category === "parquet") return PARQUET_COLUMNS;
      return COLUMNS[getCategoryGroup(this.category)];
    },
    subCategoryOptions() {
      if (this.category === "bagno") return this.bagnoSubCategoryOptions;
      return this.piastrelleSubCategoryOptions;
    },
    // [sc-48222] Opzioni del FILTRO: da quando i valori a DB sono normalizzati
    // il filtro cerca esattamente la voce selezionata, senza espansioni. Sono
    // le stesse del modale di creazione, con in testa la voce "tutte".
    subCategoryFilterOptions() {
      return [
        { value: "", label: "Tutte le tipologie" },
        ...this.subCategoryOptions,
      ];
    },
    // [sc-48248] Con una marca sola il filtro non sceglie niente: la tendina
    // compare solo se ce ne sono almeno due.
    brandFilterOptions() {
      if (!this.brands.length) return [];
      return [
        { value: "", label: "Tutti i brand" },
        ...this.brands.map((b) => ({ value: b, label: b })),
      ];
    },
    // [sc-48337] Il totale è fermato a 10.000 dall'API: oltre quella soglia si
    // mostra "10.000+" invece di un numero preciso che nessuno userebbe e che
    // costava una scansione dell'intera tabella a ogni battuta.
    // [sc-48337] La posizione la conta il client: a cursore l'API non sa a che
    // pagina siamo. Saltando alla fine con un conteggio fermo la posizione non
    // è conoscibile — `pageIndex` è null — e si dice quello che si sa: quante
    // righe si stanno guardando.
    positionLabel() {
      const n = this.items.length;
      if (!n) return "0-0";
      const perPage = this.pagination.itemsPerPage || 8;
      // Quante righe stanno fra questa pagina e l'estremo da cui si conta.
      const scarto = (this.pageIndex - 1) * perPage;
      // Stesso separatore delle migliaia del totale: "335.612-335.619 di
      // 335.635" si legge, "335612-335619 di 335.635" no.
      const fmt = (v) => v.toLocaleString("it-IT");

      if (this.anchor === "start") {
        return `${fmt(scarto + 1)}-${fmt(scarto + n)}`;
      }

      // Contando dalla fine: con un totale vero si risale alla posizione
      // assoluta, ed è esatta anche se l'ultima pagina non cade sui multipli
      // di otto.
      if (!this.pagination.totalIsCapped) {
        const ultimo = (this.pagination.totalItems ?? n) - scarto;
        return `${fmt(ultimo - n + 1)}-${fmt(ultimo)}`;
      }
      // Totale fermo a 10.000: la posizione assoluta non è conoscibile, ma la
      // distanza dalla fine sì. Meglio "ultimi 9-16" di un generico conteggio
      // che si azzera a ogni pagina.
      return `ultimi ${fmt(scarto + 1)}-${fmt(scarto + n)}`;
    },
    totalLabel() {
      const n = this.pagination.totalItems ?? 0;
      return this.pagination.totalIsCapped
        ? `${n.toLocaleString("it-IT")}+`
        : n.toLocaleString("it-IT");
    },
  },
  methods: {
    ...mapActions(["fetchItems"]),
    formatPriceToPrint,
    formatDecimal,

    // Suffisso unità del prezzo in base a dati riga (categoria + tipologia)
    priceUnit(item) {
      const name = String(item?.itemName || "").toLowerCase();
      const sub = String(item?.subCategory || "").toLowerCase();
      if (name.includes("battiscopa") || sub.includes("battiscopa"))
        return "/ml";
      if (Number(item?.sqmPerBox) > 0) return "/mq";
      if (Number(item?.mlPerBox) > 0) return "/ml";
      const group = getCategoryGroup(item?.category);
      if (group === "area") return "/mq";
      if (group === "linear") return "/ml";
      return "/pz";
    },

    // [sc-48248] Le marche sono quelle presenti a DB nella categoria, non
    // quelle della pagina mostrata. Un errore qui non blocca l'elenco: si
    // perde solo la tendina.
    async loadBrands() {
      try {
        const { data } = await this.$axios.get("/item/brands", {
          params: { category: this.category },
        });
        this.brands = Array.isArray(data) ? data : [];
      } catch (err) {
        this.brands = [];
        console.error("Errore durante il caricamento delle marche: ", err);
      }
    },

    async handleRefresh() {
      this.refreshing = true;
      this.refreshed = false;
      try {
        await this.reloadCurrent();
      } finally {
        this.refreshing = false;
        this.refreshed = true;
        setTimeout(() => {
          this.refreshed = false;
        }, 1500);
      }
    },

    openNewArticle() {
      if (this.category === "piastrelle" || this.category === "bagno") {
        this.pendingSubCategory = "";
        this.showSubCategoryModal = true;
      } else {
        this.$router.push(`/home/listini/${this.category}/nuovo`);
      }
    },
    confirmSubCategory() {
      if (!this.pendingSubCategory) return;
      this.showSubCategoryModal = false;
      this.$router.push({
        path: `/home/listini/${this.category}/nuovo`,
        query: { subCategory: this.pendingSubCategory },
      });
    },

    openArchiveModal(item) {
      this.selectedItem = item;
      this.showArchiveModal = true;
    },
    closeArchiveModal() {
      if (this.archiving) return;
      this.showArchiveModal = false;
      this.selectedItem = null;
    },

    goToEditItem(itemId) {
      this.$router.push({
        name: "listini-edit-item",
        params: { category: this.category, itemId },
      });
    },

    async onSearch() {
      await this.loadItems();
    },
    // [sc-48339] Invio: esegue subito la ricerca in attesa invece di
    // aggiungerne una. `flush()` non fa nulla se non c'è niente in coda, che è
    // il caso in cui il debounce è già scattato e i risultati sono aggiornati.
    onSearchNow() {
      this.debouncedSearch.flush();
    },
    // Il `flush()` va dopo il `nextTick`: svuotando il campo si fa scattare il
    // watcher, e annullare il debounce *prima* lo lascerebbe ripartire subito
    // dopo. Così invece la richiesta è una sola e parte senza attesa.
    onResetSearch() {
      this.searchTerm = "";
      this.$nextTick(() => this.debouncedSearch.flush());
    },
    // [sc-48337] Spostamento di una pagina, avanti o indietro.
    //
    // Il cursore da mandare è l'estremo della pagina attuale — l'ultimo id
    // andando avanti, il primo andando indietro — e l'API restituisce le otto
    // righe oltre quel confine. È questo che rende il costo indipendente dalla
    // profondità: con `OFFSET` il database doveva produrre e buttare via tutte
    // le righe precedenti, misurato 172 ms a pagina 500 contro 1,4 ms qui.
    //
    // `pageIndex` esiste solo per scrivere "17-24 di": a cursore l'API non sa a
    // che pagina siamo, e non deve saperlo.
    goToAdjacentPage(direction) {
      if (this.isLoading) return;
      const avanti = direction === "next";
      if (avanti ? !this.pagination.hasNext : !this.pagination.hasPrev) return;
      const cursor = avanti
        ? this.pagination.nextCursor
        : this.pagination.prevCursor;
      if (cursor == null) return;
      // Ancorati alla fine, "successivi" avvicina all'ultima pagina e quindi
      // fa **calare** la distanza dall'estremo.
      const verso = this.anchor === "start" ? 1 : -1;
      this.pageIndex = Math.max(1, this.pageIndex + (avanti ? verso : -verso));
      this.loadItems({ cursor, direction });
    },
    goToFirstPage() {
      if (this.isLoading) return;
      this.pageIndex = 1;
      this.loadItems();
    },
    // [sc-48337] Salto alla fine. Non serve sapere quante pagine ci sono — è
    // proprio il conteggio che si è tolto: l'API legge dalla coda invertendo
    // l'ordinamento, che costa come leggere dalla testa.
    //
    // Da qui in poi il conteggio è ancorato alla fine: vedi `positionLabel`.
    goToLastPage() {
      if (this.isLoading || !this.pagination.hasNext) return;
      this.loadItems({ direction: "prev" });
    },
    // Ricarica la pagina attualmente mostrata, senza spostarsi.
    reloadCurrent() {
      return this.loadItems({
        cursor: this.currentCursor,
        direction: this.currentDirection,
      });
    },
    async loadItems({ cursor = null, direction = "next" } = {}) {
      if (cursor == null) {
        // Senza cursore si riparte da un estremo: la prima pagina andando
        // avanti, l'ultima andando indietro. In entrambi i casi la distanza da
        // quell'estremo è 1.
        this.anchor = direction === "prev" ? "end" : "start";
        this.pageIndex = 1;
      }
      this.currentCursor = cursor;
      this.currentDirection = direction;
      this.isLoading = true;
      try {
        await this.fetchItems({
          limit: 8,
          category: this.category,
          subCategory: this.selectedSubCategory,
          brand: this.selectedBrand,
          search: this.searchTerm,
          cursor,
          direction,
          currentPage: this.pageIndex,
        });
      } catch (err) {
        this.$toast.error(
          err?.response?.data?.message ||
            "Errore durante il caricamento degli articoli."
        );
      } finally {
        this.isLoading = false;
      }
    },

    async confirmArchive() {
      if (!this.selectedItem) return;
      try {
        this.archiving = true;
        await this.$axios.post(
          `item/archive/${this.selectedItem.id}`,
          JSON.stringify({ archived: true })
        );
        this.$toast.success("Articolo archiviato con successo.");
        this.showArchiveModal = false;
        await this.reloadCurrent();
        // [sc-48337] Archiviando l'ultimo articolo di una pagina, quella pagina
        // resta vuota: senza numeri di pagina non c'è un "ultima" a cui saltare,
        // quindi si torna all'inizio dell'elenco.
        if (!this.items.length && this.pageIndex > 1) {
          this.goToFirstPage();
        }
      } catch (err) {
        this.$toast.error(
          err.response?.data?.details || "Impossibile archiviare l'articolo."
        );
      } finally {
        this.archiving = false;
        this.selectedItem = null;
      }
    },
  },
  // [sc-48339] Il debounce si crea per istanza, non a livello di modulo: due
  // pagine montate insieme condividerebbero la stessa attesa.
  created() {
    this.debouncedSearch = debounce(this.onSearch, 300);
  },
  mounted() {
    if (this.category) {
      this.loadItems();
      this.loadBrands(); // [sc-48248]
    }
  },
  beforeUnmount() {
    // Senza questo, uscendo dalla pagina subito dopo aver digitato resta una
    // chiamata in coda su un componente ormai smontato.
    this.debouncedSearch?.cancel();
  },
  watch: {
    category() {
      this.searchTerm = "";
      this.selectedSubCategory = "";
      // [sc-48248] Le marche sono di questa categoria: cambiandola, una marca
      // che lì non esiste darebbe un elenco vuoto senza spiegazione.
      this.selectedBrand = "";
      this.brands = [];
      this.loadBrands();
      this.loadItems();
      // Azzerare `searchTerm` fa scattare il watcher qui sotto: senza annullarlo
      // il cambio categoria produrrebbe due richieste identiche a 300 ms di
      // distanza.
      this.$nextTick(() => this.debouncedSearch.cancel());
    },
    selectedSubCategory() {
      this.loadItems();
    },
    selectedBrand() {
      this.loadItems(); // [sc-48248]
    },
    // [sc-48339] Era `this.onSearch()` diretto: una chiamata per ogni tasto
    // premuto. Digitando "osp" ne partivano tre, e vinceva quella che rispondeva
    // per ultima — che era la prima, la più lenta, con 312.021 risultati invece
    // di 201. Il debounce toglie le chiamate intermedie; a coprire il caso in
    // cui due si accavallino comunque c'è il `reqId` nello store.
    searchTerm() {
      this.debouncedSearch();
    },
  },
};
</script>
<style src="@/css/StockPage.css" />
